import { useParams, Link, Navigate } from "react-router-dom";
import { CalendarDays, ArrowLeft, MessageCircle, Link2, Instagram } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { SubpageHeader } from "@/components/birthlife/SubpageHeader";
import { Footer } from "@/components/birthlife/Footer";
import { Breadcrumbs } from "@/components/birthlife/Breadcrumbs";
import { Seo, breadcrumbLd, articleLd } from "@/lib/seo";
import { useSite } from "@/lib/site";
import { POST_REDIRECTS } from "@/lib/redirects";

const ytId = (v) => {
  if (!v) return "";
  const m = String(v).match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/|live\/)([\w-]{11})/);
  return m ? m[1] : /^[\w-]{11}$/.test(v) ? v : "";
};

const Block = ({ block, i }) => {
  if (block.type === "heading")
    return (
      <h2 data-testid={`post-block-heading-${i}`} className="mt-10 font-display text-2xl font-semibold text-ink sm:text-3xl">
        {block.text}
      </h2>
    );
  if (block.type === "image" && block.src)
    return (
      <figure data-testid={`post-block-image-${i}`} className="mt-8">
        <img src={block.src} alt={block.alt || ""} className="w-full rounded-2xl object-cover shadow-lift" loading="lazy" />
        {block.alt && <figcaption className="mt-2 text-center text-xs text-faint">{block.alt}</figcaption>}
      </figure>
    );
  if (block.type === "video" && ytId(block.src))
    return (
      <div data-testid={`post-block-video-${i}`} className="mt-8 overflow-hidden rounded-2xl shadow-lift">
        <iframe
          src={`https://www.youtube.com/embed/${ytId(block.src)}`}
          title="Embedded video"
          className="aspect-video w-full"
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  return (
    <p data-testid={`post-block-text-${i}`} className="mt-5 text-base leading-relaxed text-plumsoft sm:text-lg">
      {block.text}
    </p>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const { content } = useSite();
  const posts = content.posts;
  const post = (posts || []).find((p) => p.slug === slug);
  // Renamed articles: permanently redirect old truncated slugs to the current URL
  if (!post && posts && posts.length > 0 && POST_REDIRECTS[slug]) {
    return <Navigate to={`/blog/${POST_REDIRECTS[slug]}`} replace />;
  }
  const crumbs = [{ label: "Blog", href: "/blog/" }, { label: post?.title || "Article", href: `/blog/${slug}/` }];
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = post ? `${post.title} — The Birth Life` : "The Birth Life";

  const copyLink = async () => {
    window.__tblTrack?.("share_click", { channel: "copy", slug });
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — paste it in your Instagram story or DM");
  };

  const shareWhatsApp = () => {
    window.__tblTrack?.("share_click", { channel: "whatsapp", slug });
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, "_blank", "noopener");
  };

  const shareNative = async () => {
    window.__tblTrack?.("share_click", { channel: "instagram", slug });
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url: shareUrl });
      } catch {
        // dismissed
      }
    } else {
      copyLink();
    }
  };

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-sand">
      <Seo
        title={post?.title || "Article"}
        description={post?.excerpt}
        path={`/blog/${slug}/`}
        type="article"
        image={post?.cover_image || undefined}
        jsonLd={post ? [articleLd(post), breadcrumbLd(crumbs)] : [breadcrumbLd(crumbs)]}
      />
      <SubpageHeader />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-8 lg:py-16">
        <Breadcrumbs items={crumbs} />
        {!post ? (
          <div className="py-20 text-center" data-testid="post-not-found">
            <p className="font-display text-2xl text-ink">Article not found</p>
            <Link to="/blog" className="mt-4 inline-flex items-center gap-2 font-outfit text-sm font-semibold text-terra">
              <ArrowLeft className="h-4 w-4" /> Back to all articles
            </Link>
          </div>
        ) : (
          <article data-testid="post-article">
            {post.tags && (
              <p className="mt-8 font-outfit text-xs font-semibold uppercase tracking-[0.2em] text-terra" data-testid="post-tags">
                {post.tags}
              </p>
            )}
            <h1 className="mt-3 font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-5xl" data-testid="post-title">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-ink/10 pb-6 text-xs font-outfit text-faint">
              <div className="flex items-center gap-4">
                <span>By Yogyata Wadhwa, IBCLC</span>
                {post.published_at && (
                  <span className="inline-flex items-center gap-1.5" data-testid="post-date">
                    <CalendarDays className="h-3.5 w-3.5" /> {dayjs(post.published_at).format("D MMM YYYY")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2" data-testid="post-share">
                <span className="mr-1 text-[11px] uppercase tracking-widest">Share</span>
                <button
                  data-testid="share-whatsapp"
                  onClick={shareWhatsApp}
                  aria-label="Share on WhatsApp"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:scale-110"
                >
                  <MessageCircle className="h-4 w-4" />
                </button>
                <button
                  data-testid="share-instagram"
                  onClick={shareNative}
                  aria-label="Share on Instagram"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-terra text-white transition-transform hover:scale-110"
                >
                  <Instagram className="h-4 w-4" />
                </button>
                <button
                  data-testid="share-copy"
                  onClick={copyLink}
                  aria-label="Copy link"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:border-terra hover:text-terra"
                >
                  <Link2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="mt-8 w-full rounded-3xl object-cover shadow-deep" data-testid="post-cover" />
            )}
            {ytId(post.video_url) && (
              <div className="mt-8 overflow-hidden rounded-3xl shadow-deep" data-testid="post-video">
                <iframe
                  src={`https://www.youtube.com/embed/${ytId(post.video_url)}`}
                  title={post.title}
                  className="aspect-video w-full"
                  allow="encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            <div className="mt-4">
              {(post.blocks || []).map((b, i) => (
                <Block key={i} block={b} i={i} />
              ))}
            </div>

            <div className="mt-14 rounded-3xl bg-sagelight p-8 text-center">
              <p className="font-display text-2xl font-semibold text-ink">Need one-on-one support?</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-plumsoft">
                Book a consultation with Yogyata — in-clinic in Gurgaon or online worldwide.
              </p>
              <Link
                data-testid="post-book-cta"
                to="/"
                className="mt-5 inline-block rounded-full bg-terra px-7 py-3 font-outfit text-sm font-semibold text-white transition-colors hover:bg-terradark"
              >
                Book a consultation
              </Link>
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
